import { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '../App';
import { usePermissions } from '@/hooks/usePermissions';
import { api } from '../services/api';
import { PageHeader } from '@/components/PageHeader';
import { DataTable } from '@/components/DataTable';
import { EmptyState } from '@/components/EmptyState';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Badge } from '@/components/ui/Badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/Select';
import { Skeleton } from '@/components/ui/Skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import { CalendarDays, Plus, Trash2 } from 'lucide-react';

interface PlannedHour {
  id: string;
  yearMonth: string;
  hours: number;
  projectId?: string;
  project?: { name: string };
  taskId?: string;
  task?: { name: string } | null;
  userId?: string;
  user?: { name: string };
}

interface ProjectOption { id: string; name: string; }
interface TaskOption { id: string; name: string; }

export default function PlannedHoursPage() {
  const { t } = useTranslation();
  const { user } = useContext(AuthContext)!;
  const { canWrite } = usePermissions({ pageKey: 'planned-hours' });


  const [projects, setProjects] = useState<ProjectOption[]>([]);
  const [tasks, setTasks] = useState<TaskOption[]>([]);

  const [yearMonth, setYearMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [projectId, setProjectId] = useState('');
  const [taskId, setTaskId] = useState('');
  const [hours, setHours] = useState('');

  const [myList, setMyList] = useState<PlannedHour[]>([]);
  const [manageList, setManageList] = useState<PlannedHour[]>([]);
  const [loadingMy, setLoadingMy] = useState(true);
  const [loadingManage, setLoadingManage] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('my');

  // filter for manage tab
  const [filterUserId, setFilterUserId] = useState('');
  const [filterProjectId, setFilterProjectId] = useState('');
  const [filterYearMonth, setFilterYearMonth] = useState('');

  const [usersList, setUsersList] = useState<{ id: string; name: string }[]>([]);

  useEffect(() => {
    async function loadProjects() {
      try {
        const d = await api<ProjectOption[]>('/projects');
        setProjects(d || []);
      } catch {}
    }
    loadProjects();
  }, []);

  useEffect(() => {
    async function loadUsers() {
      if (!canWrite) return;
      try {
        const d = await api<{ id: string; name: string }[]>('/users');
        setUsersList(d || []);
      } catch {}
    }
    loadUsers();
  }, [canWrite]);

  useEffect(() => {
    async function loadMy() {
      setLoadingMy(true);
      try {
        const d = await api<PlannedHour[]>(`/planned-hours?userId=${user?.id || ''}`);
        setMyList(d || []);
      } catch (err: any) { setError(err.message); }
      setLoadingMy(false);
    }
    if (user) loadMy();
  }, [user]);

  useEffect(() => {
    if (!canWrite || activeTab !== 'manage') return;
    async function loadManage() {
      setLoadingManage(true);
      try {
        const params = new URLSearchParams();
        if (filterUserId) params.set('userId', filterUserId);
        if (filterProjectId) params.set('projectId', filterProjectId);
        if (filterYearMonth) params.set('yearMonth', filterYearMonth);
        const d = await api<PlannedHour[]>(`/planned-hours?${params.toString()}`);
        setManageList(d || []);
      } catch (err: any) { setError(err.message); }
      setLoadingManage(false);
    }
    loadManage();
  }, [canWrite, activeTab, filterUserId, filterProjectId, filterYearMonth]);

  useEffect(() => {
    if (!projectId) { setTasks([]); setTaskId(''); return; }
    async function loadTasks() {
      try {
        const d = await api<TaskOption[]>(`/projects/${projectId}/tasks`);
        setTasks(d || []);
      } catch {}
    }
    loadTasks();
  }, [projectId]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!yearMonth || !hours) return;
    setBusy(true); setError('');
    try {
      const payload: any = { yearMonth, hours: parseFloat(hours) };
      if (projectId) payload.projectId = projectId;
      if (taskId) payload.taskId = taskId;
      await api('/planned-hours', { method: 'POST', body: JSON.stringify(payload) });
      setHours(''); setTaskId('');
      // refresh lists
      const my = await api<PlannedHour[]>(`/planned-hours?userId=${user?.id || ''}`);
      setMyList(my || []);
      if (canWrite) {
        const params = new URLSearchParams();
        if (filterUserId) params.set('userId', filterUserId);
        if (filterProjectId) params.set('projectId', filterProjectId);
        if (filterYearMonth) params.set('yearMonth', filterYearMonth);
        const m = await api<PlannedHour[]>(`/planned-hours?${params.toString()}`);
        setManageList(m || []);
      }
    } catch (err: any) { setError(err.message); }
    finally { setBusy(false); }
  }

  async function handleDelete(id: string) {
    try { await api(`/planned-hours/${id}`, { method: 'DELETE' }); }
    catch { return; }
    setMyList(prev => prev.filter(x => x.id !== id));
    setManageList(prev => prev.filter(x => x.id !== id));
  }

  const columns = [
    { key: 'yearMonth', header: 'Month' },
    { key: 'project', header: 'Project', render: (p: PlannedHour) => p.project?.name || '-' },
    { key: 'task', header: 'Task', render: (p: PlannedHour) => p.task?.name || '-' },
    { key: 'hours', header: 'Hours', render: (p: PlannedHour) => (
      <span className="font-mono text-sm font-semibold text-[var(--color-primary)]">{Number(p.hours).toFixed(1)}h</span>
    )},
    { key: 'actions', header: '', className: 'w-10', render: (p: PlannedHour) => (
      <Button variant="ghost" size="icon" className="h-8 w-8 text-rose-600" onClick={() => handleDelete(p.id)} aria-label="Delete"><Trash2 className="h-4 w-4" /></Button>
    )},
  ];

  const mobileRender = (p: PlannedHour) => (
    <div className="space-y-1">
      <div className="text-sm font-medium text-[var(--color-foreground)]">{p.yearMonth}</div>
      <div className="text-xs text-[var(--color-muted-foreground)]">{p.project?.name || '-'} · {p.task?.name || '-'} · <span className="font-mono font-semibold text-[var(--color-primary)]">{Number(p.hours).toFixed(1)}h</span></div>
    </div>
  );

  const formSection = (
    <form onSubmit={submit} className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-5 sm:items-end">
      <div className="space-y-2">
        <Label htmlFor="ph-month">Month (YYYY-MM)</Label>
        <Input id="ph-month" value={yearMonth} onChange={e => setYearMonth(e.target.value)} placeholder="2026-07" required pattern="[0-9]{4}-[0-9]{2}" />
      </div>
      <div className="space-y-2">
        <Label>Project</Label>
        <Select value={projectId} onValueChange={setProjectId}>
          <SelectTrigger><SelectValue placeholder="Optional" /></SelectTrigger>
          <SelectContent>
            {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label>Task</Label>
        <Select value={taskId} onValueChange={setTaskId} disabled={!projectId}>
          <SelectTrigger><SelectValue placeholder={projectId ? (tasks.length ? 'Optional' : 'No tasks') : 'Select project first'} /></SelectTrigger>
          <SelectContent>
            {tasks.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label>Hours</Label>
        <Input type="number" step="0.1" value={hours} onChange={e => setHours(e.target.value)} required />
      </div>
      <div>
        <Button type="submit" loading={busy} disabled={busy} className="w-full sm:w-auto">
          <Plus className="mr-2 h-4 w-4" />
          {t('add') || 'Add'}
        </Button>
      </div>
    </form>
  );

  return (
    <div className="animate-in">
      <PageHeader title={t('planning') || 'Planning'}>
        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[var(--color-primary)]/10 text-[var(--color-primary)]">
          <CalendarDays className="h-5 w-5" />
        </div>
      </PageHeader>

      {error && <Badge variant="destructive" className="mb-4">{error}</Badge>}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-6">
          <TabsTrigger value="my">My Planning</TabsTrigger>
          {canWrite && <TabsTrigger value="manage">Manage Planning</TabsTrigger>}
        </TabsList>

        <TabsContent value="my" className="space-y-6">
          {formSection}
          {loadingMy ? (
            <div className="space-y-2"><Skeleton className="h-10 w-full" /><Skeleton className="h-12 w-full" /></div>
          ) : (
            <DataTable
              columns={columns}
              data={myList}
              keyExtractor={x => x.id}
              mobileRender={mobileRender}
              emptyState={<EmptyState icon={CalendarDays} title="No planned hours" description="Add your first monthly estimate below." />}
            />
          )}
        </TabsContent>

        {canWrite && (
          <TabsContent value="manage" className="space-y-6">
            <div className="mb-4 flex flex-wrap items-end gap-3">
              <div className="w-48 space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wider">User</Label>
                <Select value={filterUserId} onValueChange={setFilterUserId}>
                  <SelectTrigger><SelectValue placeholder="All users" /></SelectTrigger>
                  <SelectContent>{usersList.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="w-48 space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wider">Project</Label>
                <Select value={filterProjectId} onValueChange={setFilterProjectId}>
                  <SelectTrigger><SelectValue placeholder="All projects" /></SelectTrigger>
                  <SelectContent>{projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="w-48 space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wider">Month</Label>
                <Input value={filterYearMonth} onChange={e => setFilterYearMonth(e.target.value)} placeholder="YYYY-MM" />
              </div>
            </div>

            {loadingManage ? (
              <div className="space-y-2"><Skeleton className="h-10 w-full" /><Skeleton className="h-12 w-full" /></div>
            ) : (
              <DataTable
                columns={[
                  { key: 'user', header: 'User', render: (p: PlannedHour) => p.user?.name || '-' },
                  ...columns
                ]}
                data={manageList}
                keyExtractor={x => x.id}
                mobileRender={(p: PlannedHour) => (
                  <div className="space-y-1">
                    <div className="text-sm font-medium text-[var(--color-foreground)]">{p.user?.name || '-'} · {p.yearMonth}</div>
                    <div className="text-xs text-[var(--color-muted-foreground)]">{p.project?.name || '-'} · {p.task?.name || '-'} · <span className="font-mono font-semibold text-[var(--color-primary)]">{Number(p.hours).toFixed(1)}h</span></div>
                  </div>
                )}
                emptyState={<EmptyState icon={CalendarDays} title="No planned hours" description="Adjust filters or add new entries." />}
              />
            )}
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
